import { useEffect, ReactElement } from 'react';

import { Footer } from '~/components/global';
import NavHeader from '~/components/navigation/NavHeader';
import { ScrollToTop } from '~/components/ui';
import useUser from '~/lib/user';
import useGtm from '~/lib/useGtm';
import useGtag from '~/lib/useGtag';
import useExperiment from '~/lib/experiment';
import { GA4_MIGRATION_EXPERIMENT_NAME } from '~/lib/util/constants';
import { getUserData } from '~/lib/util/dataLayer';
import { DataLayerProps } from '~/types/seo';
import usePrivy from '~/lib/script/privy';

export default function BaseLayout({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { user } = useUser();
  const { setDataLayer } = useGtm();
  const { gtag, getGA4UserType } = useGtag();
  const { variant } = useExperiment(GA4_MIGRATION_EXPERIMENT_NAME);
  const isCollectionBrowsePage = (children as ReactElement)?.props
    ?.isCollectionBrowsePage;

  usePrivy();
  useEffect(() => {
    if (user && variant) {
      const dataLayerData: DataLayerProps = getUserData(user) || {};

      // TODO: Remove this once we have a GA4 migration complete
      // GA4 migration is disabled
      if (variant !== 'enabled') {
        setDataLayer(dataLayerData);
      } else {
        gtag('set', {
          userId: user.id,
          userEmail: user.email,
          userEmailHash: user.email_hash,
          hasPlus: user.has_plus,
          hasBriefcase: user.has_briefcase,
          isStaff: user.is_staff,
          isBetaling: user.is_betaling,
        });

        // Custom event to know when the user data was set and trigger tags that depends
        // on user data. i.e: The klaviyo identify tag.
        gtag('event', 'set_user_data');

        gtag('set', 'user_properties', {
          user_type: getGA4UserType(user),
        });
      }
    }
  }, [user, setDataLayer, getGA4UserType, gtag, variant]);

  return (
    <div className={className} {...props}>
      <NavHeader isCollectionBrowsePage={isCollectionBrowsePage} />
      {/* Padding for fixed header */}
      <div id="headlessui-portal-root" className="pt-[111px] md:pt-[131px]">
        {children}
      </div>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
